import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Cat, Play, RotateCcw, Trophy, Frown } from "lucide-react";
import GameHeader from "./GameHeader";
import confetti from "canvas-confetti";
import { Card } from "@/types/linguadeck";
import { playAudio } from "@/lib/audioUtils";
import { useSoundStore } from "@/stores/useSoundStore";
import { createCardQueue } from "@/lib/cardQueue";

function AnimalMouseIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      {/* Ears */}
      <ellipse cx="8" cy="6" rx="2.5" ry="3" fill="currentColor" />
      <ellipse cx="16" cy="6" rx="2.5" ry="3" fill="currentColor" />
      {/* Head/body */}
      <ellipse cx="12" cy="13" rx="5" ry="6" fill="none" stroke="currentColor" strokeWidth="2" />
      {/* Nose */}
      <circle cx="12" cy="11" r="1" fill="currentColor" />
      {/* Tail */}
      <path d="M7 16 Q4 18 2 20" stroke="currentColor" strokeWidth="2" fill="none" />
    </svg>
  );
}

function shuffleArray<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function generateOptions(currentCard: Card, allCards: Card[]): string[] {
  const correct = (currentCard.back || "").trim();
  const others = allCards.filter(
    (c) => c !== currentCard && (c.back || "").trim()
  );
  const uniqueBacks = [...new Set(others.map((c) => (c.back || "").trim()).filter(Boolean))].filter(
    (b) => b !== correct
  );
  const shuffled = shuffleArray(uniqueBacks);
  const distractors: string[] = [];
  for (let i = 0; distractors.length < 3 && i < shuffled.length * 2; i++) {
    const pick = shuffled[i % Math.max(1, shuffled.length)];
    if (pick && !distractors.includes(pick)) distractors.push(pick);
  }
  while (distractors.length < 3 && uniqueBacks[0]) {
    distractors.push(uniqueBacks[0]);
  }
  return shuffleArray([correct, ...distractors.slice(0, 3)]);
}

type GameState = "intro" | "playing" | "won" | "lost";

const WORDS_PER_LEVEL = 3;
const BASE_POINTS = 100;
const BASE_CAT_SPEED = 1.5;
const CAT_SPEED_PER_LEVEL = 0.5;

interface CatMouseModeProps {
  cards: Card[];
  lang?: string;
  onBack?: () => void;
  onProgressComplete?: (pct: number) => void;
  onFavorite?: () => void;
  isFavorite?: boolean;
  /** Per-card result for adaptive learning */
  onCardResult?: (cardId: string, correct: boolean) => void;
}

function MouseHoleCheeseIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 32"
      className={className}
      aria-hidden
    >
      {/* Dark semi-circle (mouse hole) */}
      <path
        d="M8 32 L8 16 Q24 8 40 16 L40 32 Z"
        fill="hsl(220 20% 12%)"
        stroke="hsl(220 15% 20%)"
        strokeWidth="1"
      />
      {/* Cheese wedge (yellow) */}
      <path
        d="M38 18 L42 22 L42 28 L38 24 Z"
        fill="#facc15"
        stroke="#eab308"
        strokeWidth="0.5"
      />
    </svg>
  );
}

export default function CatMouseMode({ cards, lang: deckLang, onBack, onProgressComplete, onFavorite, isFavorite = false, onCardResult }: CatMouseModeProps) {
  const { t } = useTranslation();
  const { isSoundEnabled, isVoiceEnabled, gameVolume, voiceVolume, toggleSound: storeToggleSound } = useSoundStore();
  const filteredCards = useMemo(
    () =>
      cards.filter((c) => (c.front || "").trim() && (c.back || "").trim()),
    [cards]
  );
  const totalWords = filteredCards.length;

  const [gameState, setGameState] = useState<GameState>("intro");
  const [mousePosition, setMousePosition] = useState(15);
  const [catPosition, setCatPosition] = useState(0);
  const [level, setLevel] = useState(1);
  const [wordsCleared, setWordsCleared] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(1);
  const [highScore, setHighScore] = useState(0);
  const [currentCard, setCurrentCard] = useState<Card | null>(null);
  const [options, setOptions] = useState<string[]>([]);
  const [answeredCorrect, setAnsweredCorrect] = useState(false);
  const [wrongShake, setWrongShake] = useState(false);

  const catIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cardQueueRef = useRef(createCardQueue());
  const bgMusicRef = useRef<HTMLAudioElement | null>(null);
  const winMusicRef = useRef<HTMLAudioElement | null>(null);
  const loseMusicRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      bgMusicRef.current?.pause();
      winMusicRef.current?.pause();
      loseMusicRef.current?.pause();
    };
  }, []);

  useEffect(() => {
    const vol = isSoundEnabled ? gameVolume : 0;
    if (bgMusicRef.current) { bgMusicRef.current.volume = vol; if (!isSoundEnabled) bgMusicRef.current.pause(); }
    if (winMusicRef.current) { winMusicRef.current.volume = vol; if (!isSoundEnabled) winMusicRef.current.pause(); }
    if (loseMusicRef.current) { loseMusicRef.current.volume = vol; if (!isSoundEnabled) loseMusicRef.current.pause(); }
  }, [gameVolume, isSoundEnabled]);

  useEffect(() => {
    if (gameState === "won" && isSoundEnabled) {
      bgMusicRef.current?.pause();
      if (bgMusicRef.current) bgMusicRef.current.currentTime = 0;
      winMusicRef.current?.pause();
      if (winMusicRef.current) {
        winMusicRef.current.currentTime = 0;
        winMusicRef.current.play().catch((e) => console.error("Audio play blocked:", e));
      }
    } else if (gameState === "lost" && isSoundEnabled) {
      bgMusicRef.current?.pause();
      if (bgMusicRef.current) bgMusicRef.current.currentTime = 0;
      loseMusicRef.current?.pause();
      if (loseMusicRef.current) {
        loseMusicRef.current.currentTime = 0;
        loseMusicRef.current.play().catch((e) => console.error("Audio play blocked:", e));
      }
    } else if (gameState !== "playing") {
      bgMusicRef.current?.pause();
      if (bgMusicRef.current) bgMusicRef.current.currentTime = 0;
    }
  }, [gameState, isSoundEnabled]);

  const toggleSound = useCallback(() => {
    storeToggleSound();
    if (!useSoundStore.getState().isSoundEnabled) {
      bgMusicRef.current?.pause();
      winMusicRef.current?.pause();
      loseMusicRef.current?.pause();
    } else if (gameState === "playing" && bgMusicRef.current) {
      bgMusicRef.current.currentTime = 0;
      bgMusicRef.current.volume = gameVolume;
      bgMusicRef.current.play().catch((e) => console.error("Audio play blocked:", e));
    }
  }, [gameState, storeToggleSound, gameVolume]);

  const getPoolIndices = useCallback(() => {
    return filteredCards.map((_, i) => i);
  }, [filteredCards]);

  const loadNextCard = useCallback(() => {
    const idx = cardQueueRef.current.getNext(filteredCards, getPoolIndices);
    if (idx !== null) {
      const card = filteredCards[idx];
      setCurrentCard(card);
      setOptions(generateOptions(card, filteredCards));
      const voiceOn = useSoundStore.getState().isVoiceEnabled;
      const term = (card.front || "").trim();
      if (voiceOn && term) {
        void playAudio({ card, side: "front", deckLang: deckLang ?? "en", volume: voiceVolume });
      }
    } else {
      setCurrentCard(null);
      setOptions([]);
    }
    setAnsweredCorrect(false);
  }, [filteredCards, getPoolIndices, voiceVolume, deckLang]);

  const startGame = useCallback(() => {
    cardQueueRef.current.reset();
    setGameState("playing");
    setMousePosition(15);
    setCatPosition(0);
    setLevel(1);
    setWordsCleared(0);
    setScore(0);
    setCombo(1);
    loadNextCard();
    if (isSoundEnabled && bgMusicRef.current) {
      bgMusicRef.current.currentTime = 0;
      bgMusicRef.current.play().catch(console.error);
    }
  }, [loadNextCard, isSoundEnabled]);

  const resetGame = useCallback(() => {
    if (catIntervalRef.current) {
      clearInterval(catIntervalRef.current);
      catIntervalRef.current = null;
    }
    setGameState("intro");
    setMousePosition(15);
    setCatPosition(0);
    setLevel(1);
    setWordsCleared(0);
    setScore(0);
    setCombo(1);
    setCurrentCard(null);
    setOptions([]);
  }, []);

  const tryAgain = useCallback(() => {
    winMusicRef.current?.pause();
    if (winMusicRef.current) winMusicRef.current.currentTime = 0;
    loseMusicRef.current?.pause();
    if (loseMusicRef.current) loseMusicRef.current.currentTime = 0;
    startGame();
  }, [startGame]);

  const handleCorrect = useCallback(() => {
    if (gameState !== "playing" || answeredCorrect) return;
    if (currentCard?.id && onCardResult) onCardResult(currentCard.id, true);
    setAnsweredCorrect(true);
    const nextWordsCleared = wordsCleared + 1;
    if (totalWords > 0 && onProgressComplete) {
      const pct = Math.min(100, Math.round((nextWordsCleared / totalWords) * 100));
      onProgressComplete(pct);
    }
    setWordsCleared((prev) => {
      const next = prev + 1;
      setLevel(1 + Math.floor(next / WORDS_PER_LEVEL));
      return next;
    });
    setScore((prev) => {
      const newLevel = 1 + Math.floor(nextWordsCleared / WORDS_PER_LEVEL);
      const points = BASE_POINTS * newLevel * combo;
      return prev + points;
    });
    setCombo((prev) => Math.min(prev + 1, 5));
    setMousePosition((prev) => {
      const next = Math.min(100, prev + 10);
      if (next >= 100) {
        if (nextWordsCleared >= totalWords) {
          setGameState("won");
        } else {
          setCatPosition(0);
          setLevel((l) => l + 1);
          if (catIntervalRef.current) {
            clearInterval(catIntervalRef.current);
            catIntervalRef.current = null;
          }
          return 15;
        }
        if (catIntervalRef.current) {
          clearInterval(catIntervalRef.current);
          catIntervalRef.current = null;
        }
        return next;
      }
      return next;
    });
    setTimeout(loadNextCard, 400);
  }, [gameState, answeredCorrect, wordsCleared, combo, loadNextCard, totalWords, isVoiceEnabled, voiceVolume, currentCard, deckLang, onProgressComplete, onCardResult]);

  const handleWrong = useCallback(() => {
    if (gameState !== "playing" || answeredCorrect) return;
    if (currentCard?.id && onCardResult) onCardResult(currentCard.id, false);
    setAnsweredCorrect(true);
    setCombo(1);
    setWrongShake(true);
    setTimeout(() => setWrongShake(false), 400);
    setCatPosition((prev) => Math.min(100, prev + 10));
    setTimeout(loadNextCard, 500);
  }, [gameState, answeredCorrect, loadNextCard, currentCard?.id, onCardResult]);

  const handleSelectOption = useCallback(
    (option: string) => {
      if (!currentCard || answeredCorrect) return;
      const isCorrect = option === (currentCard.back || "").trim();
      if (isCorrect) {
        handleCorrect();
      } else {
        handleWrong();
      }
    },
    [currentCard, answeredCorrect, handleCorrect, handleWrong]
  );

  useEffect(() => {
    if (gameState !== "playing") return;

    const catSpeed = BASE_CAT_SPEED + CAT_SPEED_PER_LEVEL * level;

    catIntervalRef.current = setInterval(() => {
      setCatPosition((prev) => Math.min(100, prev + catSpeed));
    }, 1000);

    return () => {
      if (catIntervalRef.current) {
        clearInterval(catIntervalRef.current);
        catIntervalRef.current = null;
      }
    };
  }, [gameState, level]);

  useEffect(() => {
    if (gameState !== "playing") return;
    if (catPosition >= mousePosition) {
      setGameState("lost");
      if (catIntervalRef.current) {
        clearInterval(catIntervalRef.current);
        catIntervalRef.current = null;
      }
    }
  }, [gameState, catPosition, mousePosition]);

  useEffect(() => {
    if ((gameState === "won" || gameState === "lost") && score > highScore) {
      setHighScore(score);
    }
  }, [gameState, score, highScore]);


  useEffect(() => {
    if (gameState === "won") {
      const colors = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"];
      const fire = (ratio: number, opts: confetti.Options = {}) => {
        confetti({
          ...opts,
          colors,
          origin: opts.origin ?? { y: 0.7 },
          particleCount: Math.floor(200 * ratio),
          shapes: ["circle", "square"],
          scalar: opts.scalar ?? 1.2,
        });
      };
      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91 });
    }
  }, [gameState]);

  useEffect(() => {
    return () => {
      if (catIntervalRef.current) {
        clearInterval(catIntervalRef.current);
      }
    };
  }, []);

  const topNavRow = (
    <GameHeader
      onBack={onBack}
      centerContent={
        gameState === "playing" ? (
          <div className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
            {t("catMouse.wordsProgress", { cleared: wordsCleared, total: totalWords })}
          </div>
        ) : undefined
      }
    />
  );

  if (filteredCards.length < 4) {
    return (
      <>
        <audio ref={bgMusicRef} src="/bg-music.mp3" loop preload="auto" />
        <audio ref={winMusicRef} src="/win.mp3" preload="auto" />
        <audio ref={loseMusicRef} src="/lose.mp3" preload="auto" />
        <div className="flex flex-col w-full max-w-4xl mx-auto">
          {topNavRow}
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <p className="text-lg font-medium text-[hsl(var(--foreground))]">
          {t('catMouse.needCards')}
        </p>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
          {t('catMouse.addMoreCards')}
        </p>
          </div>
        </div>
      </>
    );
  }

  if (gameState === "intro") {
    return (
      <>
        <audio ref={bgMusicRef} src="/bg-music.mp3" loop preload="auto" />
        <audio ref={winMusicRef} src="/win.mp3" preload="auto" />
        <audio ref={loseMusicRef} src="/lose.mp3" preload="auto" />
        <div className="flex flex-col w-full max-w-4xl mx-auto">
          {topNavRow}
          <div className="flex flex-col items-center justify-center w-full max-w-lg mx-auto py-12 px-6">
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-[hsl(var(--primary))]/20 blur-2xl rounded-full scale-150" />
          <div className="relative flex gap-4">
            <Cat
              className="w-16 h-16 text-amber-500 shrink-0"
              strokeWidth={2}
              aria-hidden
            />
            <AnimalMouseIcon className="w-16 h-16 text-emerald-600 shrink-0" />
          </div>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-[hsl(var(--foreground))] text-center mb-3">
          {t('catMouse.title')}
        </h1>
        <p className="text-[hsl(var(--muted-foreground))] text-center mb-8 max-w-sm">
          {t('catMouse.subtitle')}
        </p>
        <button
          type="button"
          onClick={startGame}
          className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white bg-[hsl(var(--accent2))] hover:opacity-90 transition-all hover:scale-105 active:scale-95 shadow-lg"
        >
          <Play className="w-6 h-6" strokeWidth={2.5} />
          {t('catMouse.startGame')}
        </button>
          </div>
        </div>
      </>
    );
  }

  const statsEndScreen = (
    <div className="flex flex-col w-full max-w-lg mx-auto py-12 px-6">
      {gameState === "won" ? (
        <Trophy
          className="w-20 h-20 text-yellow-500 mx-auto mb-6"
          strokeWidth={2}
          aria-hidden
        />
      ) : (
        <Frown
          className="w-24 h-24 text-gray-400/80 mx-auto mb-6"
          strokeWidth={2}
          aria-hidden
        />
      )}
      <h1 className="text-2xl md:text-3xl font-bold text-[hsl(var(--foreground))] text-center mb-8">
        {gameState === "won" ? t("catMouse.victory") : t("catMouse.catCaught")}
      </h1>
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("defender.yourScore")}
          </p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{score}</p>
        </div>
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("defender.wordsCleared")}
          </p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{wordsCleared}</p>
        </div>
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("defender.yourLevel")}
          </p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{level}</p>
        </div>
        <div className="rounded-xl border-2 border-[hsl(var(--border))] bg-white dark:bg-slate-800 p-4 text-center shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">
            {t("defender.highScore")}
          </p>
          <p className="text-2xl font-bold text-amber-500">{Math.max(highScore, score)}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={tryAgain}
        className="flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white bg-[hsl(var(--primary))] hover:opacity-90 transition-all w-full max-w-xs mx-auto"
      >
        <RotateCcw className="w-5 h-5" />
        {t("catMouse.tryAgain")}
      </button>
    </div>
  );

  if (gameState === "won" || gameState === "lost") {
    return (
      <>
        <audio ref={bgMusicRef} src="/bg-music.mp3" loop preload="auto" />
        <audio ref={winMusicRef} src="/win.mp3" preload="auto" />
        <audio ref={loseMusicRef} src="/lose.mp3" preload="auto" />
        <div className="flex flex-col w-full max-w-4xl mx-auto">
          {topNavRow}
          {statsEndScreen}
        </div>
      </>
    );
  }

  return (
    <>
      <audio ref={bgMusicRef} src="/bg-music.mp3" loop preload="auto" />
      <audio ref={winMusicRef} src="/win.mp3" preload="auto" />
      <audio ref={loseMusicRef} src="/lose.mp3" preload="auto" />
      <div className="flex flex-col w-full max-w-4xl mx-auto">
        {topNavRow}
        <div className="flex flex-col w-full max-w-2xl mx-auto gap-8 py-6 px-4">
      {/* Race track */}
      <div className="relative w-full h-14 rounded-2xl bg-[hsl(var(--bg3))] border-2 border-[hsl(var(--border))] overflow-visible">
        <div className="absolute inset-0 rounded-xl flex items-center px-2">
          <div className="flex-1 h-2 rounded-full bg-[hsl(var(--muted))]" />
        </div>
        {/* Finish line: mouse hole + cheese at right */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 z-0 flex items-center">
          <MouseHoleCheeseIcon className="w-14 h-10 text-[hsl(var(--foreground))]" />
        </div>
        <div
          className="absolute top-1/2 -translate-y-1/2 transition-all duration-500 ease-in-out z-10 flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500/90 shadow-lg border-2 border-white dark:border-gray-800"
          style={{ left: `calc(${mousePosition}% - 20px)` }}
        >
          <AnimalMouseIcon className="w-5 h-5 text-white shrink-0" />
        </div>
        <div
          className="absolute top-1/2 -translate-y-1/2 transition-all duration-500 ease-in-out z-10 flex items-center justify-center w-10 h-10 rounded-full bg-amber-500/90 shadow-lg border-2 border-white dark:border-gray-800"
          style={{ left: `calc(${catPosition}% - 20px)` }}
        >
          <Cat className="w-5 h-5 text-white" strokeWidth={2.5} aria-hidden />
        </div>
      </div>

      {/* Flashcard */}
      <div
        className={`flex flex-col items-center justify-center rounded-2xl border-2 border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] p-8 min-h-[140px] transition-transform ${
          wrongShake ? "animate-shake" : ""
        }`}
      >
        <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
          {t('catMouse.word')}
        </p>
        <p className="text-2xl md:text-3xl font-bold text-[hsl(var(--foreground))] text-center break-words">
          {currentCard?.front ?? "..."}
        </p>
      </div>

      {/* Options grid 2x2 */}
      <div className="grid grid-cols-2 gap-4">
        {options.filter(Boolean).map((opt, idx) => (
          <button
            key={`${opt}-${idx}`}
            type="button"
            onClick={() => handleSelectOption(opt)}
            disabled={answeredCorrect}
            className="min-h-[72px] px-6 py-4 rounded-xl font-semibold text-base text-left transition-all duration-200 border-2 border-[hsl(var(--border))] bg-[hsl(var(--bg3))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-[hsl(var(--border))] disabled:hover:bg-[hsl(var(--bg3))] active:scale-[0.98]"
          >
            {opt}
          </button>
        ))}
      </div>
        </div>
      </div>
    </>
  );
}
